import { getLocale } from '@/lib/paraglide/runtime'

// VND thực tế không có subunit, nhưng ERP có kịch bản bán theo kg với đơn giá
// lẻ (vd 2222,123 đ/kg) nên hiển thị giữ tối đa 3 chữ số thập phân. Pin locale
// vi-VN để tránh Windows en-US leak dấu phẩy.
const vndFormatter = new Intl.NumberFormat('vi-VN', {
    maximumFractionDigits: 3,
})

export function formatVND(n: number) {
    return vndFormatter.format(n)
}

/** Format with ₫ currency symbol appended */
export function formatVnd(n: number) {
    return vndFormatter.format(n) + ' ₫'
}

// Số lượng tồn / SL bán: hiển thị có dấu phân cách hàng nghìn (vi-VN dùng "."),
// tối đa 3 chữ số thập phân để cover các đơn vị kg lẻ. Cố định locale 'vi-VN'
// tránh leak format của OS (Windows en-US sẽ ra dấu phẩy).
const qtyFormatter = new Intl.NumberFormat('vi-VN', {
    maximumFractionDigits: 3,
})

export function formatQty(n: number) {
    return qtyFormatter.format(n)
}

/** Format datetime with full date + time (day/month/year hour:minute) */
export function formatDateTime(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString(getLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Format datetime compact — day/month hour:minute (no year, for list cards) */
export function formatDateTimeShort(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString(getLocale(), {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Format datetime kiểu vi-VN default (HH:mm:ss d/M/yyyy) + FORCE timezone
 *  Asia/Ho_Chi_Minh. Server lưu UTC, nghiệp vụ VN — helper này đảm bảo
 *  hiển thị đúng giờ +7 bất kể browser ở múi nào (cron headless, dev nước
 *  ngoài). Output format khớp với stock-in.tsx (đã chuẩn) — dùng helper này
 *  ở mọi chỗ hiện thời gian audit / lịch sử để đồng bộ. */
export function formatDateTimeVN(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** Format date-only vi-VN (d/M/yyyy) timezone Asia/Ho_Chi_Minh. Dùng cho
 *  field thuần DATE (purchase_date, processed_at) — không có giờ. */
export function formatDateVN(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** Helper THỐNG NHẤT cho mọi field thời gian từ server (FE ↔ BE contract).
 *
 *  Contract:
 *  - Server (Go) trả time qua JSON theo RFC3339 (`2026-06-21T10:30:00Z`) cho
 *    cột TIMESTAMPTZ và ISO 8601 date-only (`2026-06-21`) cho cột DATE.
 *  - FE chỉ dùng `formatVN()` cho mọi field thời gian — tự detect.
 *  - Cả 2 trường hợp đều ép timezone Asia/Ho_Chi_Minh để khớp nghiệp vụ VN
 *    bất kể browser ở múi nào.
 *
 *  Quy tắc detect: input khớp pattern `YYYY-MM-DD` đứng riêng → date-only,
 *  ngược lại (có 'T' hoặc 'Z') → datetime.
 */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

export function formatVN(value: string): string {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    const opts: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Ho_Chi_Minh' }
    if (DATE_ONLY_RE.test(value)) {
        return d.toLocaleDateString('vi-VN', opts)
    }
    return d.toLocaleString('vi-VN', opts)
}
